// Refresh-token rotation, ported from the add-in's refreshAuth.ts: single-flight, and a `401
// refresh_token_reused` (the API revoked the family) clears the session instead of retrying.
import { clearTokens, readToken, writeToken } from './tokenStore';
import type { Tokens } from '../types';

export const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';
const REFRESH_PATH = '/token/refresh';

let inFlight: Promise<boolean> | null = null;
let accessTokenAtRefreshStart: string | null = null;

export function getAccessToken(): string | null {
  return readToken('accessToken');
}

export function getRefreshToken(): string | null {
  return readToken('refreshToken');
}

export function storeTokens(tokens: Tokens): void {
  writeToken('accessToken', tokens.access_token);
  writeToken('tokenType', tokens.token_type || 'bearer');
  if (tokens.refresh_token) writeToken('refreshToken', tokens.refresh_token);
}

export function clearAuth(): void {
  clearTokens();
}

export function authHeader(): Record<string, string> {
  const token = readToken('accessToken');
  const type = readToken('tokenType') ?? 'bearer';
  return token ? { Authorization: `${type} ${token}` } : {};
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  // Another tab may have rotated since we last looked: adopt its access token.
  const current = getAccessToken();
  if (accessTokenAtRefreshStart !== null && current !== null && current !== accessTokenAtRefreshStart) return true;
  const response = await fetch(`${BASE_URL}${REFRESH_PATH}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).catch(() => null);
  if (!response) return false;
  if (!response.ok) {
    if (response.status === 401) clearTokens(); // rejected, reused or expired: the session is over
    return false;
  }
  try {
    const data = (await response.json()) as Tokens;
    storeTokens(data);
    return true;
  } catch {
    return false;
  }
}

export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight;
  accessTokenAtRefreshStart = getAccessToken();
  inFlight = doRefresh().finally(() => {
    inFlight = null;
    accessTokenAtRefreshStart = null;
  });
  return inFlight;
}

// The refresh token alone is enough for the API to revoke the session, so a sign-out after the access token expired
// still closes it on the server; the bearer goes along when there is one.
export async function logout(): Promise<void> {
  const header = authHeader();
  const refreshToken = getRefreshToken();
  if (refreshToken || header.Authorization) {
    await fetch(`${BASE_URL}/logout`, {
      method: 'POST',
      headers: { accept: 'application/json', 'Content-Type': 'application/json', ...header },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => null);
  }
  clearTokens();
}
